import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { useParams, useSearchParams } from 'react-router-dom';

import { Button } from '../../components/general';
import { useSequence } from '../../components/layout/headerSideNav/components/useSequence';
import { ErrorModal } from '../../components/modals';

import { useWindowWidth } from '../../hooks/useWindowWidth';

import { getEventPublic } from '../../services/api/events';
import {
  getFaceWithFiles,
  getFaceWithFilesById,
} from '../../services/checkInService';

import { useAttendeeStore } from '../../stores/attendee';
import { useUserStore } from '../../stores/user';

import { PublicEvent, PageError, ModalError } from '../../types';
import { goTo, toggleAdminMode } from '../../utils';

import CheckInPageUI from './checkInPageUI';

const EventCheckIn = () => {
  const { eventId = '', faceId = '' } = useParams();

  const [searchParams] = useSearchParams();
  const reqAssets = searchParams.get('reqAssets') ?? '';

  const { face, setFace } = useAttendeeStore();
  const { user, adminMode, setAdminMode } = useUserStore();

  const windowWidth = useWindowWidth();

  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [step, setStep] = useState(0);

  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<PageError>(null);
  const [error, setError] = useState<ModalError>(null);

  const didMountRef = useRef(false);

  const isEventEnded = (event: PublicEvent) => {
    return (
      moment(event.endDate).isBefore(moment().utc(), 'day') ||
      event.stage.toLowerCase() !== 'upload'
    );
  };

  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const fetchedEvent = await getEventPublic(eventId);

        if (!fetchedEvent) {
          setPageError(404);
          throw new Error('Event data is null or undefined');
        }

        setEvent(fetchedEvent);

        // if face data is already in store -> don't fetch the data
        if (face) return;

        // personalised missing assets page
        if (faceId) {
          const faceResult = await getFaceWithFilesById(eventId, faceId);

          if (!faceResult || !reqAssets)
            setError({
              heading: 'Sorry, this link is invalid',
              subheading: (
                <>
                  But you can learn more about CrowdClip&reg;{' '}
                  <a href="https://crowdclip.com/">here</a>.
                </>
              ),
              onClick: () => goTo('https://crowdclip.com/'),
            });

          setFace(faceResult);

          return;
        }

        // general missing assets page
        if (reqAssets) return;

        // if event is ended or analysis started – show error
        if (isEventEnded(fetchedEvent))
          setError({
            heading: 'Sorry, this event has ended',
            subheading: (
              <>
                But you can learn more about CrowdClip&reg;{' '}
                <a href="https://crowdclip.com/">here</a>.
              </>
            ),
            onClick: () => goTo('https://crowdclip.com/'),
          });
      } catch (error) {
        setError({
          subheading: 'Error fetching some data',
          isTryAgainBtn: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (!didMountRef.current) {
      fetchEventData();
      didMountRef.current = true;
    }
  }, [eventId, face, faceId, reqAssets, setFace]);

  useEffect(() => {
    if (event && isEventEnded(event) && adminMode && error) setError(null);
  }, [event, adminMode, error]);

  useEffect(() => {
    if (face && step === 0) setStep(1);
  }, [face, step]);

  const refetchFace = async () => {
    if (!face) return;

    const faceTemp = await getFaceWithFiles(eventId, face, true);
    setFace(faceTemp);
  };

  useSequence({
    handleAction: () => user && toggleAdminMode(user, adminMode, setAdminMode),
  });

  if (!event || (error && !pageError))
    return (
      <ErrorModal
        show={!!error}
        heading={error?.heading ?? 'Something went wrong'}
        subheading={error?.subheading}
        btn={
          error?.onClick && (
            <Button className="w-full" onClick={error?.onClick}>
              OK :(
            </Button>
          )
        }
        isTryAgainBtn={error?.isTryAgainBtn}
        isContactUs
      />
    );

  return (
    <CheckInPageUI
      width={windowWidth}
      isLoading={isLoading}
      pageError={pageError}
      title="Check-In"
      step={step}
      setStep={setStep}
      event={event}
      face={face ?? undefined}
      reqAssets={reqAssets}
      refetchFace={refetchFace}
    />
  );
};

export default EventCheckIn;
